/* eslint-disable max-lines */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useUser } from '../../contexts/user';
import { useCart } from '../../contexts/cart';
import {
  FaMapMarkerAlt,
  FaChevronRight,
  FaCreditCard,
  FaStore,
  FaUniversity,
  FaRegFileAlt,
  FaTimes,
} from 'react-icons/fa';
import { TiStarburst } from 'react-icons/ti';
import { GiCycling } from 'react-icons/gi';
import { FiZap } from 'react-icons/fi';
import StripeLogo from '../../assets/stripe-logo.svg';
import PayOnDelivery from '../../assets/pay-on-delivery.svg';
import Kiapay from '../../assets/kiapay.svg';
import SpinnerIcon from '../../assets/spinner2.svg';
import NeoboxLogo from '../../assets/neobox-logo.png';
import AppliedCouponIcon from '../../assets/applied-coupon.svg';
import AppliedCouponIcon2 from '../../assets/applied-coupon2.svg';
import Confetti from 'canvas-confetti';
import SelectAddressModal from '../../components/CheckOut/SelectAddressModal';
import SingleProductDetails from '../../components/MerchantDetails/SingleProductDetails';
import CouponModal from '../../components/CheckOut/CouponModal';
import log from '../../utils/logger';
import { getApiErrorMessage } from '../../utils/apiError';
import api from '../../api/api';
import useToast from '../../hooks/useToast';
import Toast from '../../components/Toast';

const Checkout = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { idToken, logout, adminImpersonate, user } = useUser();
  const { cartItems, setCart, totalPrice, clearCart, cartId } = useCart();
  const [limitMessage, setLimitMessage] = useState('');
  const [selectedOption, setSelectedOption] = useState('delivery');
  const [deliveryType, setDeliveryType] = useState('asap');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [isSelectAddressModalOpen, setIsSelectAddressModalOpen] =
    useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectAddressClicked, setSelectAddressClicked] = useState(false);
  const [selectOrderTypeClicked, setSelectOrderTypeClicked] = useState(false);
  const [selectDeliveryTypeClicked, setSelectDeliveryTypeClicked] =
    useState(false);
  const [clickedPaymentMethod, setClickedPaymentMethod] = useState(null);
  const [checkoutClicked, setCheckoutClicked] = useState(false);
  const [applyCouponClicked, setApplyCouponClicked] = useState(false);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const { toastMessage, toastType, showToast } = useToast();
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [appliedCouponDiscount, setAppliedCouponDiscount] = useState(0);
  const [discountedItems, setDiscountedItems] = useState([]);
  const [showBillDetails, setShowBillDetails] = useState(false);
  const [expandedTaxes, setExpandedTaxes] = useState(false);
  const [expandedCoupon, setExpandedCoupon] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [, setIsCalculatingDelivery] = useState(false);
  const couponRef = useRef(null);
  const [editingQuantities, setEditingQuantities] = useState({});

  const paymentIcons = {
    etransfer: FaCreditCard,
    bankDeposit: FaUniversity,
    payOnDelivery: PayOnDelivery,
    stripe: StripeLogo,
    kiapay: Kiapay,
  };

  const isReactIcon = (icon) =>
    typeof icon === 'function';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  useEffect(() => {
    if (!selectedAddress || cartItems.length === 0) {
      setDeliveryFee(0);
      return;
    }

    const timeout = setTimeout(() => {
      calculateDeliveryFee();
    }, 300);

    return () => clearTimeout(timeout);
  }, [selectedAddress, cartItems]);

  useEffect(() => {
    if (!appliedCoupon) { return; }

    requestAnimationFrame(() => {
      const rect = couponRef.current.getBoundingClientRect();

      Confetti({
        particleCount: 120,
        spread: 80,
        startVelocity: 35,
        origin: {
          x: (rect.left + rect.width / 2) / window.innerWidth,
          y: (rect.top + rect.height / 2) / window.innerHeight,
        },
      });
    });

  }, [appliedCoupon]);

  const handleSelectAddressModal = () => {
    setIsSelectAddressModalOpen(!isSelectAddressModalOpen);
  };

  const syncCartToBackend = async (itemsToSync = cartItems) => {
    try {
      const items = itemsToSync.map((item) => ({
        productId: item.productId,
        variantId: item.variantId,
        quantity: item.quantity,
        merchantConfigId: item.merchantConfigId,
      }));

      await api.put(
        '/buyer/cart/updateCart',
        {
          cartItems: items,
          adminImpersonateToken: adminImpersonate || null,
          buyerEmail: user?.email,
          buyerId: user?.id,
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        },
      );
    } catch (error) {
      log.error('Failed to sync cart:', error);
      const message = error.response?.data?.error || '';
      if (message === 'TOKEN_EXPIRED' || message === 'INVALID_ID_TOKEN') {
        navigate('/home');
        sessionStorage.setItem('sessionExpired', 'true');
        sessionStorage.setItem(
          'toast',
          JSON.stringify({
            message: t('sessionExpiredMessage'),
            type: 'error',
          }),
        );
        logout();
        clearCart();
      } else {
        showToast(getApiErrorMessage(error, t), 'error');
      }
    }
  };

  const updateItemQuantity = async (item, delta) => {
    try {
      const minQty = item.minQuantity || 1;
      const maxQty = item.maxQuantity || Infinity;
      const newQuantity = item.quantity + delta;

      if (newQuantity > maxQty) {
        showLimitMessage(t('maxQuantityMessage', { qty: maxQty }));
        return;
      }

      let updatedCart;

      if (newQuantity < minQty) {
        updatedCart = cartItems.filter(
          (i) =>
            !(
              i.productId === item.productId &&
              i.merchantConfigId === item.merchantConfigId &&
              i.variantId === item.variantId
            ),
        );
      } else {
        updatedCart = cartItems.map((i) =>
          i.productId === item.productId &&
          i.merchantConfigId === item.merchantConfigId &&
          i.variantId === item.variantId
            ? { ...i, quantity: newQuantity }
            : i,
        );
      }

      setCart(updatedCart);
      await syncCartToBackend(updatedCart);
    } catch (err) {
      log.error('❌ Error updating item quantity:', err);
    }
  };

  const showLimitMessage = (msg) => {
    setLimitMessage(msg);
    setTimeout(() => setLimitMessage(''), 3000);
  };

  const getItemKey = (item) =>
    `${item.productId}-${item.variantId || 'default'}`;

  const handleQuantityInput = (item, value) => {
    if (value === '') {
      setEditingQuantities((prev) => ({
        ...prev,
        [getItemKey(item)]: '',
      }));
      return;
    }

    if (!/^\d+$/.test(value)) {
      return;
    }

    setEditingQuantities((prev) => ({
      ...prev,
      [getItemKey(item)]: value,
    }));
  };

  const handleQuantityBlur = async (item) => {
    const key = getItemKey(item);

    const rawValue =
    editingQuantities[key] !== undefined
      ? editingQuantities[key]
      : item.quantity;

    let quantity = Number(rawValue);

    const minQty = item.minQuantity || 1;
    const maxQty = item.maxQuantity || Infinity;

    if (Number.isNaN(quantity) || rawValue === '') {
      quantity = minQty;
    }

    if (quantity > maxQty) {
      showLimitMessage(t('maxQuantityMessage', { qty: maxQty }));
      quantity = maxQty;
    }

    if (quantity < minQty) {
      quantity = 0;
    }

    const newQuantity = quantity;

    let updatedCart;

    if (newQuantity <= 0) {
      updatedCart = cartItems.filter(
        (i) =>
          !(
            i.productId === item.productId &&
            i.merchantConfigId === item.merchantConfigId &&
            i.variantId === item.variantId
          ),
      );
    } else {
      updatedCart = cartItems.map((i) =>
        i.productId === item.productId &&
        i.merchantConfigId === item.merchantConfigId &&
        i.variantId === item.variantId
          ? { ...i, quantity: newQuantity }
          : i,
      );
    }

    setCart(updatedCart);
    await syncCartToBackend(updatedCart);

    setEditingQuantities((prev) => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const handleQuantityKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const fetchProductById = async (productId) => {
    try {
      const response = await api.get(`/buyer/store/products/${productId}`);
      return response.data.response.data;
    } catch (error) {
      log.error('Error fetching product:', error);
      throw error;
    }
  };

  const handlePersonalizeProduct = async (productId) => {
    try {
      const product = await fetchProductById(productId);
      setSelectedProduct(product);
    } catch (error) {
      log.error('Failed to personalize product:', error);
    }
  };

  const handleSelectAddressClick = () => {
    setSelectAddressClicked(false);
    requestAnimationFrame(() => setSelectAddressClicked(true));
  };

  const handleSelectOrderTypeClick = () => {
    setSelectOrderTypeClicked(false);
    requestAnimationFrame(() => setSelectOrderTypeClicked(true));
  };

  const handleSelectDeliveryTypeClick = () => {
    setSelectDeliveryTypeClicked(false);
    requestAnimationFrame(() => setSelectDeliveryTypeClicked(true));
  };

  const handlePaymentClick = (methodId) => {
    setClickedPaymentMethod(null);

    requestAnimationFrame(() => {
      setClickedPaymentMethod(methodId);
    });
  };

  const handleCheckoutClick = () => {
    setCheckoutClicked(false);
    requestAnimationFrame(() => setCheckoutClicked(true));
  };

  const handleApplyCouponClick = () => {
    setApplyCouponClicked(false);
    requestAnimationFrame(() => setApplyCouponClicked(true));
  };

  const fetchPaymentMethods = async () => {
    try {
      const res = await api.get('/buyer/order/payment-methods', {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
        params: {
          adminImpersonateToken: adminImpersonate || null,
        },
      });

      const methods = res.data.response.data.paymentMethods;

      setPaymentMethods(methods);

      // default selection
      if (methods.length > 0) {
        setPaymentMethod(methods[0].id);
      }
    } catch (error) {
      log.error('Failed to fetch payment methods:', error.response?.data);
    }
  };

  const discountMap = useMemo(() => {
    const map = new Map();

    discountedItems.forEach(item => {
      const key = `${item.productId}_${item.variantId || ''}`;
      map.set(key, item);
    });

    return map;
  }, [discountedItems]);

  const roundMoney = (value) =>
    Math.round((Number(value) + Number.EPSILON) * 100) / 100;

  const getTaxSummary = () => {
    // Group taxes by tax combination
    const taxGroups = {};
    let totalTaxes = 0;

    cartItems.forEach((item) => {
      const originalItem = cartItems.find(i =>
        i.productId === item.productId &&
        i.variantId === item.variantId,
      );

      const tax = item.tax || 0;

      const discountKey = `${item.productId}_${item.variantId || ''}`;

      const discountedItem = discountMap.get(discountKey);

      const itemSubtotal =
        discountedItem?.discountedSubtotal ?? item.price * item.quantity;

      const itemTaxes = roundMoney(itemSubtotal * (tax / 100));

      totalTaxes = roundMoney(totalTaxes + itemTaxes);

      const taxKey = `${tax}`;

      if (!taxGroups[taxKey]) {
        taxGroups[taxKey] = {
          tax,
          subtotal: 0,
          taxes: 0,
          items: [],
        };
      }

      taxGroups[taxKey].subtotal += itemSubtotal;
      taxGroups[taxKey].taxes += itemTaxes;
      taxGroups[taxKey].items.push({
        name: `${i18n.language === 'en' ? originalItem.enName : originalItem.frName}${
          originalItem.enVariantName && originalItem.frVariantName
            ? ` - ${
              i18n.language === 'en'
                ? originalItem.enVariantName
                : originalItem.frVariantName
            }`
            : ''
        }`,
        quantity: item.quantity,
        subtotal: itemSubtotal,
        taxAmount: itemTaxes,
      });
    });
    return { taxGroups, totalTaxes };
  };

  const { taxGroups, totalTaxes } = useMemo(
    () => getTaxSummary(),
    [cartItems, i18n.language, discountedItems],
  );

  const calculateDeliveryFee = async () => {
    if (!selectedAddress || cartItems.length === 0) {
      setDeliveryFee(0);
      return;
    }

    try {
      setIsCalculatingDelivery(true);

      const response = await api.post(
        '/buyer/order/calculateDeliveryFee',
        {
          address: {
            latitude: selectedAddress.latitude,
            longitude: selectedAddress.longitude,
          },
          cartItems: cartItems.map((item) => ({
            productId: item.productId,
            variantId: item.variantId,
            quantity: item.quantity,
            merchantConfigId: item.merchantConfigId,
          })),
          adminImpersonateToken: adminImpersonate || null,
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        },
      );

      setDeliveryFee(response.data.response.data.deliveryFee || 0);
    } catch (error) {
      const message = error.response?.data?.error || '';
      log.error('Failed to calculate delivery fee:', error);
      if (message === 'TOKEN_EXPIRED' || message === 'INVALID_ID_TOKEN') {
        navigate('/home');
        sessionStorage.setItem('sessionExpired', 'true');
        sessionStorage.setItem(
          'toast',
          JSON.stringify({
            message: t('sessionExpiredMessage'),
            type: 'error',
          }),
        );
        logout();
        clearCart();
      }
      setDeliveryFee(0);
    } finally {
      setIsCalculatingDelivery(false);
    }
  };

  const subtotalAfterDiscount = roundMoney(
    Math.max(0, totalPrice - appliedCouponDiscount),
  );

  const grandTotal = roundMoney(
    Math.max(
      0,
      subtotalAfterDiscount +
      roundMoney(deliveryFee) +
      totalTaxes,
    ),
  );

  const originalTotalTaxes = roundMoney(
    cartItems.reduce((sum, item) => {
      const subtotal = item.price * item.quantity;
      return sum + subtotal * ((item.tax || 0) / 100);
    }, 0),
  );

  const originalGrandTotal = roundMoney(
    totalPrice + deliveryFee + originalTotalTaxes,
  );

  const totalSavings = roundMoney(
    originalGrandTotal - grandTotal,
  );

  const handleSubmit = async () => {
    try {
      if (!selectedAddress) {
        showToast(t('selectAddressFirst'), 'warning');
        return;
      }

      setIsCreatingCheckout(true);

      const payload = {
        merchantConfigId: cartItems[0]?.merchantConfigId,
        orderType: selectedOption.toLowerCase(),
        deliveryType,
        address: selectedAddress,
        cartItems,
        subtotalBeforeDiscount: totalPrice,
        subtotalAfterDiscount,
        deliveryFee,
        coupon: appliedCoupon,
        discountedItems,
        grandTotal,
        totalTaxes,
        paymentMethod,
        adminImpersonateToken: adminImpersonate || null,
        buyerEmail: user?.email,
        buyerId: user?.id,
      };

      const res = await api.post('/buyer/order/createOrder', payload, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      const { order, orderNumber, timestamp, store } = res.data.response.data;

      if (!cartId) {
        log.warn('No cart ID found. Skipping cart deletion.');
      } else {
        await api.delete(`/buyer/cart/deleteCart/${cartId}`, {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
          data: {
            adminImpersonateToken: adminImpersonate || null,
            buyerEmail: user?.email,
            buyerId: user?.id,
          },
        });
        clearCart();
      }

      navigate('/profile/track-order', {
        state: { order, orderNumber, timestamp, store, justPurchased: true },
      });
    } catch (err) {
      log.error('Error submitting order:', err.response);
      const message = err.response?.data?.error || '';
      if (message === 'TOKEN_EXPIRED' || message === 'INVALID_ID_TOKEN') {
        navigate('/home');
        sessionStorage.setItem('sessionExpired', 'true');
        sessionStorage.setItem(
          'toast',
          JSON.stringify({
            message: t('sessionExpiredMessage'),
            type: 'error',
          }),
        );
        logout();
        clearCart();
      }
      else {
        showToast(getApiErrorMessage(err, t), 'error');
      }
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  const handleOpenCouponModal = () => {
    setShowCouponModal(true);
  };

  const handleCloseCouponModal = () => {
    setShowCouponModal(false);
  };

  const handleCouponApplied = ({ coupon, discountAmount, discountedItems }) => {
    setAppliedCoupon(coupon);
    setAppliedCouponDiscount(
      roundMoney(discountAmount || 0),
    );
    const enriched = (discountedItems || []).map((item) => {
      const cartItem = cartItems.find(
        (ci) =>
          ci.productId === item.productId &&
          ci.variantId === item.variantId,
      );

      return {
        ...item,
        name:
          i18n.language === 'en'
            ? cartItem?.enName
            : cartItem?.frName,

        variantName:
          i18n.language === 'en'
            ? cartItem?.enVariantName
            : cartItem?.frVariantName,
      };
    });

    setDiscountedItems(
      enriched.map(item => ({
        ...item,
        discount: roundMoney(item.discount),
        discountedSubtotal: roundMoney(item.discountedSubtotal),
      })),
    );
  };

  return (
    <>
      <style>
        {`
          .radio-mark {
            position: relative;
            background-color: #ecececff;
            border: 2px solid rgb(217, 217, 223);
          }
          .radio-mark::after {
            content: "";
            display: none;
            position: absolute;
            top: 50%;
            left: 50%;
            width: 8px;
            height: 8px;
            border-radius: 9999px;
            background: white;
            transform: translate(-50%, -50%);
          }
          input[type="radio"]:checked + .radio-mark::after {
            display: block;
            background-color: #111827; /* black inner dot */
          }
        `}
      </style>

      <style>{`
        @keyframes slow-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .slow-spin {
          animation: slow-spin 7s linear infinite;
        }

        .custom-green {
          color: #49ac8eff;
        }

        .custom-green-bg {
          background-color: #cff3e6ff;
        }
      `}</style>

      <section className="flex flex-col min-h-dvh">
        <Header />

        {/* Header for Small Devices  */}

        {cartItems.length === 0 ? (
          <div className="text-center py-20 px-4 flex flex-col items-center gap-6">
            <h2 className="text-xl font-semibold text-gray-800">
              {t('cartEmptyTitle')}
            </h2>
            <p className="text-gray-600">{t('cartEmptyMessage')}</p>
            <button
              onClick={() => navigate('/home')}
              className="px-6 py-3 bg-black text-white rounded-lg hover:bg-blue-500 hover:text-black font-medium transition-colors"
            >
              {t('exploreProducts')}
            </button>
          </div>
        ) : (
          <>
            <main className="flex-grow min-h-0 bg-white font-sans text-gray-600 lg:py-6 pb-[calc(100px+env(safe-area-inset-bottom,0px))] lg:pb-0">
              <section className="bg-white font-sans text-black py-6">
                <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left panel */}
                  <div className="order-2 lg:order-1 border border-gray-800 bg-gray-300 rounded-xl p-4 flex flex-col gap-4 self-start overflow-y-auto max-h-[80dvh] lg:max-h-none">
                    {/* Lieu de livraison */}
                    <button
                      className="border border-gray-800 bg-white rounded-xl p-4 transition-colors duration-300 hover:bg-[#dedede] relative"
                      onClick={() => {
                        handleSelectAddressClick();
                        handleSelectAddressModal();
                      }}
                    >
                      {/* Pulse effect */}
                      {selectAddressClicked && (
                        <span
                          className="absolute inset-0 bg-[#888888] opacity-30 rounded animate-pulse-select"
                          onAnimationEnd={() => setSelectAddressClicked(false)}
                        />
                      )}
                      <div className="text-[12px] font-bold uppercase mb-2 select-none text-left">
                        {t('deliveryLocation')}
                      </div>
                      <div
                        className="w-full flex items-center justify-between text-sm font-normal"
                      >
                        <div className="flex items-start gap-2">
                          {/* Marker icon */}
                          <FaMapMarkerAlt className="text-lg text-black mt-1" />

                          {/* Text block */}
                          {selectedAddress &&
                          selectedAddress.addressNumber &&
                          selectedAddress.formattedAddress &&
                          selectedAddress.latitude &&
                          selectedAddress.longitude ? (
                              <div className="flex flex-col items-start">
                                <span className="font-semibold text-black text-sm">
                                  {t(selectedAddress.addressType)}
                                </span>
                                <span className="text-black text-xs line-clamp-2 text-left">
                                  {selectedAddress.addressNumber},&nbsp;
                                  {selectedAddress.formattedAddress}
                                </span>
                              </div>
                            ) : (
                              <span className="text-[#777] text-sm">
                                {t('selectAddress')}
                              </span>
                            )}
                        </div>

                        <FaChevronRight className="text-xs text-black" />
                      </div>
                    </button>

                    {/* Type de commande */}
                    <div className="border border-gray-800 bg-white rounded-xl pb-2">
                      <div className="text-[12px] font-bold uppercase mb-2 select-none px-4 pt-4">
                        {t('orderTypeLabel')}
                      </div>
                      {['delivery'].map((option) => (
                        <label
                          key={option}
                          className="flex items-center p-4 gap-2 cursor-pointer select-none mb-1 hover:bg-[#dedede] relative overflow-hidden"
                          onClick={handleSelectOrderTypeClick}
                        >
                          {/* Pulse effect */}
                          {selectOrderTypeClicked && (
                            <span
                              className="absolute inset-0 bg-[#888888] opacity-30 rounded animate-pulse-select"
                              onAnimationEnd={() =>
                                setSelectOrderTypeClicked(false)
                              }
                            />
                          )}

                          {option === 'delivery' ? (
                            <GiCycling className="text-black text-xl" />
                          ) : (
                            <FaStore className="text-black text-xl" />
                          )}

                          <span className="text-sm font-semibold">
                            {t(option)}
                          </span>

                          <span className="ml-auto flex items-center">
                            <input
                              type="radio"
                              name="orderType"
                              checked={selectedOption === option}
                              onChange={() => setSelectedOption(option)}
                              className="hidden"
                            />
                            <span className="radio-mark inline-block w-4 h-4 rounded-full" />
                          </span>
                        </label>
                      ))}
                    </div>

                    {/* Type de livraison */}
                    <div className="border border-gray-800 bg-white rounded-xl pb-3">
                      <div className="text-[12px] font-bold uppercase pt-4 px-4 mb-2 select-none">
                        {t('deliveryTypeLabel')}
                      </div>
                      <label
                        className="flex items-center gap-2 p-4 cursor-pointer select-none hover:bg-[#dedede] relative overflow-hidden"
                        onClick={handleSelectDeliveryTypeClick}
                      >
                        {/* Pulse effect */}
                        {selectDeliveryTypeClicked && (
                          <span
                            className="absolute inset-0 bg-[#888888] opacity-30 rounded animate-pulse-select"
                            onAnimationEnd={() =>
                              setSelectDeliveryTypeClicked(false)
                            }
                          />
                        )}

                        <FiZap className="text-black text-xl" />
                        <span className="text-sm font-semibold">
                          {t('asapDelivery')}
                        </span>

                        <span className="ml-auto flex items-center">
                          <input
                            type="radio"
                            name="deliveryType"
                            checked={deliveryType === 'asap'}
                            onChange={() => setDeliveryType('asap')}
                            className="hidden"
                          />
                          <span className="radio-mark inline-block w-4 h-4 rounded-full" />
                        </span>
                      </label>
                    </div>

                    {/* Détails de la facture MOBILE ONLY */}
                    <button
                      className="lg:hidden bg-white rounded-xl p-3 text-xs font-semibold select-none flex-col items-start space-y-2 w-full"
                      onClick={() => setShowBillDetails((prev) => !prev)}
                    >
                      <div className="text-sm font-bold uppercase text-black">
                        {t('Bill Details')}
                      </div>
                      <div className="flex items-center justify-between w-full">
                        <span className="flex items-center gap-2 text-black text-sm">
                          <FaRegFileAlt className="text-black w-4 h-4" />{' '}
                          {t('grandTotal')}
                        </span>
                        <span className="flex items-center gap-2 text-sm">
                          {grandTotal.toFixed(2)}$
                          <FaChevronRight className={`text-xs transition-transform duration-300 ${showBillDetails ? 'rotate-90' : ''}`} />
                        </span>
                      </div>
                      {showBillDetails && (
                        <div className="w-full text-left space-y-1 pt-2 border-t border-gray-200">
                          <div className="flex justify-between text-sm">
                            <span>{t('Total')}</span>
                            <span>{totalPrice.toFixed(2)}$</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>{t('deliveryFee')}</span>
                            <span>{deliveryFee.toFixed(2)}$</span>
                          </div>

                          {appliedCoupon && (
                            <div className="w-full">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedCoupon((prev) => !prev);
                                }}
                                className="flex w-full items-center justify-between text-left text-sm font-medium text-green-600"
                              >
                                <span>{t('Coupon discount')}</span>
                                <span className="flex items-center gap-1">
                                  -{appliedCouponDiscount.toFixed(2)}$
                                  <FaChevronRight
                                    className={`transition-transform ${expandedCoupon ? 'rotate-90' : ''}`}
                                  />
                                </span>
                              </button>

                              {expandedCoupon && (
                                <div className="mt-2 w-full pl-3 space-y-1 text-xs text-[#999]">
                                  {discountedItems.map((item, idx) => (
                                    <div key={idx} className="flex justify-between gap-2">
                                      <span className="pr-2">
                                        {item.quantity}x {item.name}
                                        {item.variantName ? ` - ${item.variantName}` : ''}
                                      </span>
                                      <span className="text-right whitespace-nowrap">
                                        -{item.discount.toFixed(2)}$
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          <div className="flex justify-between text-sm">
                            <span>{t('Subtotal')}</span>
                            <span>{subtotalAfterDiscount.toFixed(2)}$</span>
                          </div>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedTaxes((prev) => !prev);
                            }}
                            className="flex w-full flex-col items-start text-left text-sm font-medium"
                          >
                            <div className="flex w-full items-center justify-between">
                              <span>{t('taxes')}</span>
                              <span className="flex items-center gap-1">
                                {totalTaxes.toFixed(2)}$
                                <FaChevronRight
                                  className={`transition-transform ${expandedTaxes ? 'rotate-90' : ''}`}
                                />
                              </span>
                            </div>

                            {expandedTaxes && (
                              <div className="mt-2 w-full pl-3 space-y-2">
                                {Object.entries(taxGroups).map(([key, taxGroup]) => (
                                  <div key={key} className="space-y-1 text-sm">
                                    <div className="flex justify-between font-medium">
                                      <span>{taxGroup.tax}% tax</span>
                                      <span>{taxGroup.taxes.toFixed(2)}$</span>
                                    </div>

                                    <div className="pl-2 space-y-2 text-xs text-[#999]">
                                      {taxGroup.items.map((item, index) => (
                                        <div key={index} className="flex w-full justify-between gap-2">
                                          <span className="pr-4">
                                            {item.quantity}x {item.name}
                                          </span>
                                          <span className="text-right whitespace-nowrap">
                                            {item.taxAmount.toFixed(2)}$
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </button>

                          <div className="flex justify-between text-sm font-bold">
                            <span>{t('grandTotal')}</span>
                            <span>{grandTotal.toFixed(2)}$</span>
                          </div>
                        </div>
                      )}
                    </button>

                    {/* Mode de paiement */}
                    <div className="border border-gray-800 bg-white rounded-xl p-4 space-y-2">
                      <div className="text-[12px] font-bold uppercase select-none mb-3">
                        {t('Payment Method')}
                      </div>
                      {paymentMethods
                        .filter((method) => method.enabled)
                        .map((method) => {
                          const Icon = paymentIcons[method.id] || FaCreditCard;

                          return (
                            <label
                              key={method.id}
                              className="flex items-center cursor-pointer rounded-lg p-2 border border-gray-200 hover:border-gray-300 hover:bg-[#dedede] relative overflow-hidden"
                              onClick={() => handlePaymentClick(method.id)}
                            >
                              {clickedPaymentMethod === method.id && (
                                <span
                                  className="absolute inset-0 bg-[#888888] opacity-30 rounded animate-pulse-select"
                                  onAnimationEnd={() => setClickedPaymentMethod(null)}
                                />
                              )}

                              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                                {isReactIcon(Icon) ? (
                                  <Icon className={`w-full h-full text-black ${method.id === 'etransfer' ? 'text-green-600' : ''}`} />
                                ) : (
                                  <img
                                    src={Icon}
                                    alt={paymentMethod}
                                    className="w-full h-full object-contain"
                                  />
                                )}
                              </div>

                              <div className="flex flex-col ml-3">
                                <span className="text-sm font-semibold text-black">
                                  {i18n.language === 'fr'
                                    ? method.label.fr
                                    : method.label.en}
                                </span>

                                <span className="text-xs font-normal text-[#777]">
                                  {method.subtitle}
                                </span>
                              </div>

                              <span className="ml-auto flex items-center">
                                <input
                                  type="radio"
                                  name="payment"
                                  checked={paymentMethod === method.id}
                                  onChange={() => setPaymentMethod(method.id)}
                                  className="hidden"
                                />
                                <span className="radio-mark relative inline-block w-4 h-4 rounded-full mt-1" />
                              </span>
                            </label>
                          );
                        })
                      }
                    </div>
                  </div>

                  {/* Right panel */}
                  <div className="order-1 lg:order-2 border border-gray-800 rounded-xl overflow-hidden flex flex-col self-start">
                    {/* Brand */}
                    <div className="bg-white px-4 py-3 flex items-center gap-3">
                      <img
                        alt="Logo"
                        src={NeoboxLogo}
                        className="w-8 h-8 object-contain cursor-pointer"
                        onClick={() => navigate('/home')}
                      />
                      <span className="font-bold text-sm select-none">
                        {t('neoboxCheckout')}
                      </span>
                    </div>

                    {/* Middle section */}
                    <div className="p-4 bg-gray-300 flex flex-col gap-4">
                      {/* Cart items */}
                      <div className="border border-gray-800 bg-white rounded-lg p-3 space-y-4">
                        {cartItems.map((item, index) => (
                          <div key={index} className="flex items-center gap-3">
                            <img
                              alt={item.enName}
                              src={item.images?.[0] || ''}
                              className="w-10 h-10 object-contain rounded"
                            />

                            <div className="flex flex-col justify-between text-xs font-semibold space-y-1">
                              <span>
                                {i18n.language === 'en' ? item.enName : item.frName}
                                {item.enVariantName && item.frVariantName
                                  ? ` - ${i18n.language === 'en' ? item.enVariantName : item.frVariantName}`
                                  : ''}
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="text-black font-semibold">
                                  {item.price.toFixed(2)}$
                                </span>
                                <button
                                  className="text-xs text-blue-600 font-semibold select-none"
                                  onClick={() =>
                                    handlePersonalizeProduct(item.productId)
                                  }
                                >
                                  {t('Personnaliser')}
                                </button>
                              </div>
                            </div>

                            <div className="ml-auto flex items-center gap-1 bg-black text-white rounded-md px-2 py-1 select-none h-8">
                              <button
                                onClick={() => updateItemQuantity(item, -1)}
                                className="w-5 h-5 flex items-center justify-center text-sm font-bold leading-none"
                              >
                                −
                              </button>
                              <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={
                                  editingQuantities[getItemKey(item)] ?? item.quantity
                                }
                                onChange={(e) =>
                                  handleQuantityInput(item, e.target.value)
                                }
                                onBlur={() => handleQuantityBlur(item)}
                                onKeyDown={(e) => handleQuantityKeyDown(e)}
                                className="w-7 text-center text-sm font-semibold bg-transparent outline-none"
                              />
                              <button
                                onClick={() => updateItemQuantity(item, 1)}
                                className="w-5 h-5 flex items-center justify-center text-sm font-bold leading-none"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        ))}

                        <button
                          onClick={() => navigate('/home')}
                          className="text-xs text-black font-semibold select-none border border-gray-800 rounded px-2 py-1"
                        >
                          {t('addItems')}
                        </button>

                        <input
                          type="text"
                          placeholder={t('Enter message here')}
                          className="w-full border border-gray-800 rounded-md text-xs px-3 py-2 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-400"
                        />
                      </div>

                      {/* Coupons */}
                      <button
                        className="relative overflow-hidden flex items-center justify-between border border-gray-800 bg-white rounded-xl p-3 text-sm font-semibold select-none hover:bg-[#dedede]"
                        onClick={() => {
                          handleApplyCouponClick();
                          handleOpenCouponModal();
                        }}
                      >
                        {/* Pulse effect */}
                        {applyCouponClicked && (
                          <span
                            className="absolute inset-0 bg-[#888888] opacity-30 rounded animate-pulse-select"
                            onAnimationEnd={() => setApplyCouponClicked(false)}
                          />
                        )}

                        <span className="flex items-center gap-2">
                          <img
                            src={AppliedCouponIcon2}
                            className="w-5 h-5"
                            alt="Ticket"
                          />
                          {t('Use Coupon')}
                        </span>

                        <FaChevronRight className="text-xs" />
                      </button>

                      {/* Applied Coupon */}
                      {appliedCoupon && (
                        <>
                          <style>
                            {`
                              @keyframes shine {
                                0% {
                                  transform: translateX(-200%) rotate(25deg);
                                }

                                20% {
                                  transform: translateX(1200%) rotate(25deg);
                                }

                                40% {
                                  transform: translateX(-200%) rotate(25deg);
                                }

                                100% {
                                  transform: translateX(-200%) rotate(25deg);
                                }
                              }

                              .coupon-shine {
                                position: absolute;
                                inset: 0;
                                overflow: hidden;
                                pointer-events: none;
                              }

                              .coupon-shine::after {
                                content: "";

                                position: absolute;
                                top: -50%;
                                left: -20%;

                                width: 20%;
                                height: 200%;

                                background: rgba(255, 255, 255, 0.82);

                                filter: blur(4px);

                                transform: rotate(25deg);

                                animation: shine 2.5s ease-in-out infinite;
                              }
                            `}
                          </style>

                          <div
                            ref={couponRef}
                            className="flex items-stretch overflow-hidden rounded-xl shadow-sm coupon-zoom-fade"
                          >
                            {/* Left Ticket */}
                            <div className="relative flex items-stretch justify-center overflow-hidden">
    
                              {/* Ticket SVG */}
                              <img
                                src={AppliedCouponIcon}
                                className="h-full w-[70px] object-fill"
                                alt="Ticket"
                              />

                              {/* Shine animation */}
                              <div className="coupon-shine absolute inset-0 z-20 pointer-events-none" />

                            </div>


                            {/* Right Content */}
                            <div className="relative flex-1 bg-white border border-gray-800 px-4 py-3">
    
                              <button
                                type="button"
                                onClick={() => {
                                  setAppliedCoupon(null);
                                  setAppliedCouponDiscount(0);
                                  setDiscountedItems([]);
                                }}
                                className="absolute right-3 top-3 text-[#555] transition hover:text-red-600"
                              >
                                <FaTimes className="h-[14px] w-[14px]" />
                              </button>

                              {/* Mobile view */}
                              <div className="block lg:hidden pr-8 mt-2">
                                <div className="text-xs font-medium tracking-wide text-[#444]">
                                  {t('Coupon applied: {{code}}', {
                                    code: appliedCoupon.name,
                                  })}
                                </div>

                                <div className="text-lg font-semibold text-black">
                                  {t('Mobile You saved: {{amount}}', {
                                    amount: appliedCouponDiscount.toFixed(2),
                                  })}
                                </div>
                              </div>

                              {/* Desktop view */}
                              <div className="hidden lg:block pr-8 mt-2">
                                <div className="text-xs font-medium tracking-wide text-[#444]">
                                  {t('Coupon applied: {{code}}', {
                                    code: appliedCoupon.name + ' - ' + appliedCoupon.code,
                                  })}
                                </div>

                                <div className="text-lg font-semibold text-black">
                                  {t('You saved: {{amount}}', {
                                    amount: appliedCouponDiscount.toFixed(2),
                                  })}
                                </div>
                              </div>

                            </div>
                          </div>
                        </>
                      )}

                      {/* Summary Section */}
                      <hr className="hidden lg:block border-t border-gray-800 -mx-4 my-2" />

                      {/* Détails de la facture */}
                      <button
                        className="hidden lg:flex bg-white rounded-xl p-3 text-xs font-semibold select-none flex-col items-start space-y-2 w-full"
                        onClick={() => setShowBillDetails((prev) => !prev)}
                      >
                        <div className="text-sm font-bold uppercase text-black">
                          {t('Bill Details')}
                        </div>

                        <div className="flex items-center justify-between w-full">
                          <span className="flex items-center gap-2 text-black text-sm">
                            <FaRegFileAlt className="text-black w-4 h-4" />{' '}
                            {t('grandTotal')}
                          </span>
                          <span className="flex items-center gap-2 text-sm">
                            {grandTotal.toFixed(2)}$
                            <FaChevronRight className={`text-xs transition-transform duration-300 ${showBillDetails ? 'rotate-90' : ''}`} />
                          </span>
                        </div>
                        {showBillDetails && (
                          <div className="w-full text-left space-y-1 pt-2 border-t border-gray-200">
                            <div className="flex justify-between text-sm">
                              <span>{t('Total')}</span>
                              <span>{totalPrice.toFixed(2)}$</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span>{t('deliveryFee')}</span>
                              <span>{deliveryFee.toFixed(2)}$</span>
                            </div>

                            {appliedCoupon && (
                              <div className="w-full">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedCoupon((prev) => !prev);
                                  }}
                                  className="flex w-full items-center justify-between text-left text-sm font-medium text-green-600"
                                >
                                  <span>{t('Coupon discount')}</span>
                                  <span className="flex items-center gap-1">
                                    -{appliedCouponDiscount.toFixed(2)}$
                                    <FaChevronRight
                                      className={`transition-transform ${expandedCoupon ? 'rotate-90' : ''}`}
                                    />
                                  </span>
                                </button>

                                {expandedCoupon && (
                                  <div className="mt-2 w-full pl-3 space-y-1 text-xs text-[#999]">
                                    {discountedItems.map((item, idx) => (
                                      <div key={idx} className="flex justify-between gap-2">
                                        <span className="pr-2">
                                          {item.quantity}x {item.name}
                                          {item.variantName ? ` - ${item.variantName}` : ''}
                                        </span>
                                        <span className="text-right whitespace-nowrap">
                                          -{item.discount.toFixed(2)}$
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}

                            <div className="flex justify-between text-sm">
                              <span>{t('Subtotal')}</span>
                              <span>{subtotalAfterDiscount.toFixed(2)}$</span>
                            </div>

                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedTaxes((prev) => !prev);
                              }}
                              className="flex w-full flex-col items-start text-left text-sm font-medium"
                            >
                              <div className="flex w-full items-center justify-between">
                                <span>{t('taxes')}</span>

                                <span className="flex items-center gap-1">
                                  {totalTaxes.toFixed(2)}$
                                  <FaChevronRight
                                    className={`transition-transform ${expandedTaxes ? 'rotate-90' : ''}`}
                                  />
                                </span>
                              </div>

                              {expandedTaxes && (
                                <div className="mt-2 w-full pl-3 space-y-2">
                                  {Object.entries(taxGroups).map(([key, taxGroup]) => (
                                    <div key={key} className="space-y-1 text-sm">
                                      <div className="flex justify-between font-medium">
                                        <span>{taxGroup.tax}% tax</span>
                                        <span>{taxGroup.taxes.toFixed(2)}$</span>
                                      </div>

                                      <div className="pl-2 space-y-2 text-xs text-[#999]">
                                        {taxGroup.items.map((item, index) => (
                                          <div key={index} className="flex w-full justify-between gap-2">
                                            <span className="pr-4">
                                              {item.quantity}x {item.name}
                                            </span>

                                            <span className="text-right whitespace-nowrap">
                                              {item.taxAmount.toFixed(2)}$
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </button>

                            <div className="flex justify-between text-sm font-bold">
                              <span>{t('grandTotal')}</span>
                              <span>{grandTotal.toFixed(2)}$</span>
                            </div>
                          </div>
                        )}
                      </button>

                      {/* Épargne */}
                      <div className="flex custom-green-bg rounded-xl px-4 p-2 items-center justify-between text-xs font-bold custom-green select-none">
                        <span className="flex items-center gap-2">
                          <div className="relative w-6 h-6">
                            <TiStarburst className="w-full h-full slow-spin" />
                            <span className="absolute inset-0 flex items-center justify-center text-[11px] text-white font-light">
                              %
                            </span>
                          </div>
                          {t('totalSavings')}
                        </span>
                        <span>{totalSavings.toFixed(2)}$</span>
                      </div>

                      {/* Submit */}
                      <div className="hidden lg:flex bg-white rounded-xl p-3 text-xs font-semibold select-none flex-col items-start space-y-2 w-full">
                        <button
                          onClick={() => {
                            handleCheckoutClick();
                            handleSubmit();
                          }}
                          className="bg-black text-white text-xs font-bold uppercase rounded-lg py-3 w-full select-none hover:bg-[#33b0d4] hover:text-black transition duration-200 relative overflow-hidden"
                        >
                          {/* Pulse effect */}
                          {checkoutClicked && (
                            <span
                              className="absolute inset-0 bg-[#666] opacity-30 rounded animate-pulse-select"
                              onAnimationEnd={() => setCheckoutClicked(false)}
                            />
                          )}
                          {isCreatingCheckout ? (
                            <img
                              src={SpinnerIcon}
                              alt="Loading..."
                              className="h-6 w-6 animate-spin mx-auto"
                            />
                          ) : (
                            t('createCheckout')
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <div className="flex-grow"></div>
            </main>
            {/* Submit MOBILE ONLY */}
            <div
              className="lg:hidden bottom-0 left-0 bg-white fixed p-3 text-xs font-semibold border border-gray-800 select-none flex flex-col items-center justify-center space-y-2 w-full z-40"
              style={{
                minHeight: '90px',
                paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))',
              }}
            >
              <button
                onClick={() => {
                  handleCheckoutClick();
                  handleSubmit();
                }}
                className="bg-black text-white text-xs h-[50px] font-bold uppercase rounded-lg py-3 w-full select-none hover:bg-[#33b0d4] hover:text-black transition duration-200 relative overflow-hidden"
              >
                {/* Pulse effect */}
                {checkoutClicked && (
                  <span
                    className="absolute inset-0 bg-[#666] opacity-30 rounded animate-pulse-select"
                    onAnimationEnd={() => setCheckoutClicked(false)}
                  />
                )}
                {isCreatingCheckout ? (
                  <img
                    src={SpinnerIcon}
                    alt="Loading..."
                    className="h-6 w-6 animate-spin mx-auto"
                  />
                ) : (
                  t('createCheckout')
                )}
              </button>
            </div>
          </>
        )}
      </section>

      {limitMessage && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-[#1B1223AA] text-white px-4 py-2 rounded-lg shadow-lg z-50 text-sm font-medium">
          {limitMessage}
        </div>
      )}

      <SelectAddressModal
        onClose={handleSelectAddressModal}
        isSelectAddressModalOpen={isSelectAddressModalOpen}
        onSelectAddress={(address) => {
          setSelectedAddress(address);
        }}
      />

      {selectedProduct && (
        <SingleProductDetails
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}

      <CouponModal
        isCouponModalOpen={showCouponModal}
        onClose={handleCloseCouponModal}
        cartItems={cartItems}
        onCouponApplied={handleCouponApplied}
      />

      <Toast message={toastMessage} type={toastType} />

      {/* Footer  */}
      <Footer />
    </>
  );
};

export default Checkout;
